import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material'
import {
  Restaurant,
  ArrowBack,
  Assignment,
  CheckCircle
} from '@mui/icons-material'
import { useAuth } from '../../contexts/AuthContext'
import api from '../../utils/axios'
import { differenceInDays, parseISO, addYears } from 'date-fns'

const ProdutorEditais = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [editais, setEditais] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [dialogConcorrer, setDialogConcorrer] = useState(false)
  const [editalSelecionado, setEditalSelecionado] = useState(null)
  const [alimentosEdital, setAlimentosEdital] = useState([])
  const [alimentosSelecionados, setAlimentosSelecionados] = useState([])
  const [loadingAlimentos, setLoadingAlimentos] = useState(false)
  const [loadingConcorrer, setLoadingConcorrer] = useState(false)

  const isCAFActive = () => {
    if (!user?.data_inscricao_caf) return false
    const inscricaoDate = parseISO(user.data_inscricao_caf)
    const expirationDate = addYears(inscricaoDate, 3)
    console.log(expirationDate);
    const today = new Date()
    return today <= expirationDate
  }

  useEffect(() => {
    fetchEditais()
  }, [])

  const fetchEditais = async () => {
    try {
      setLoading(true)
      const response = await api.get('/editais/list.php')
      console.log(response.data.editais);
      if (response.data.success) {
        const editaisWithConcorrentes = await Promise.all(
          (response.data.editais || []).map(async (edital) => {
            try {
              const concorrentesResponse = await api.get(
                `/editais/concorrentes.php?edital_id=${edital.id}`
              )
              console.log('teste');
              return {
                ...edital,
                concorrentes: concorrentesResponse.data.concorrentes || []
              }
            } catch (err) {
              return { ...edital, concorrentes: [] }
            }
          })
        )
        console.log(editaisWithConcorrentes);
        setEditais(editaisWithConcorrentes)
      } else {
        setError(response.data.message || 'Erro ao carregar editais')
      }
    } catch (err) {
      setError('Erro ao carregar editais. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const handleAbrirConcorrer = async (edital) => {
    if (!isCAFActive()) {
      setError('Seu CAF está expirado. Atualize sua data de inscrição no perfil.')
      return
    }
    setEditalSelecionado(edital)
    setDialogConcorrer(true)
    setAlimentosSelecionados([])
    setLoadingAlimentos(true)
    setError('')
    try {
      const response = await api.put('/editaisprodutos/list.php', { idedital: edital.id, idprodutor: user.id })
      if (response.data.success) {
        setAlimentosEdital(response.data.alimentos || [])
      } else {
        setAlimentosEdital([])
        setError(response.data.message || 'Erro ao carregar alimentos do edital')
      }
    } catch (err) {
      setAlimentosEdital([])
      setError(err.response?.data?.message || 'Erro ao carregar alimentos do edital')
    } finally {
      setLoadingAlimentos(false)
    }
  }

  const handleToggleAlimento = (alimentoId) => {
    setAlimentosSelecionados((prev) =>
      prev.includes(alimentoId)
        ? prev.filter((id) => id !== alimentoId)
        : [...prev, alimentoId]
    )
  }

  const handleFecharDialog = () => {
    setDialogConcorrer(false)
    setEditalSelecionado(null)
    setAlimentosEdital([])
    setAlimentosSelecionados([])
  }

  const handleConfirmarConcorrer = async () => {
    if (!editalSelecionado) return
    setLoadingConcorrer(true)
    setError('')
    try {
      const response = await api.post('/editais/concorrer.php', {
        edital_id: editalSelecionado.id,
        produtor_id: user.id,
        alimento_ids: alimentosSelecionados
      })
      if (response.data.success) {
        setSuccess('Inscrição realizada com sucesso!')
        handleFecharDialog()
        fetchEditais()
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(response.data.message || 'Erro ao se inscrever no edital')
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao se inscrever no edital')
    } finally {
      setLoadingConcorrer(false)
    }
  }

  const isInscrito = (edital) => {
    return edital.concorrentes?.some(c => c.produtor_id === user?.id)
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/produtor')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Restaurant sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Editais Disponíveis
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {!isCAFActive() && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Seu CAF está expirado. Você não pode se inscrever em editais até atualizar
            sua data de inscrição no perfil.
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : editais.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Assignment sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Nenhum edital disponível no momento
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {editais.map((edital) => {
              const inscrito = isInscrito(edital)
              const hoje = new Date()
              const dataFim = new Date(edital.data_fim)
              const emVigor = hoje <= dataFim

              if (!emVigor) return null

              return (
                <Card key={edital.id} elevation={2}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6">{edital.titulo}</Typography>
                      <Chip
                        label={inscrito ? 'Inscrito' : 'Disponível'}
                        color={inscrito ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>

                    <Typography variant="body2" color="text.secondary" paragraph>
                      {edital.descricao}
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Início:</strong>{' '}
                        {new Date(edital.data_inicio).toLocaleDateString('pt-BR')}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Fim:</strong>{' '}
                        {new Date(edital.data_fim).toLocaleDateString('pt-BR')}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {edital.concorrentes?.length || 0} concorrente(s)
                      </Typography>

                      {inscrito ? (
                        <Chip
                          icon={<CheckCircle />}
                          label="Você está inscrito"
                          color="success"
                          variant="outlined"
                        />
                      ) : (
                        <Button
                          variant="contained"
                          startIcon={<Assignment />}
                          onClick={() => handleAbrirConcorrer(edital)}
                          disabled={!isCAFActive()}
                        >
                          Concorrer
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )
            })}
          </Box>
        )}
      </Container>

      <Dialog open={dialogConcorrer} onClose={handleFecharDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Concorrer ao edital {editalSelecionado?.titulo ? `"${editalSelecionado.titulo}"` : ''}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selecione os alimentos que você pode fornecer para este edital:
          </Typography>
          {loadingAlimentos ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : alimentosEdital.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhum alimento cadastrado para este edital no momento.
            </Typography>
          ) : (
            <FormGroup>
              {alimentosEdital.map((alimento) => (
                <FormControlLabel
                  key={alimento.id}
                  control={
                    <Checkbox
                      checked={alimentosSelecionados.includes(alimento.id)}
                      onChange={() => handleToggleAlimento(alimento.id)}
                    />
                  }
                  label={`${alimento.nome}${alimento.medida ? ` (${alimento.medida})` : ''}`}
                />
              ))}
            </FormGroup>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFecharDialog}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleConfirmarConcorrer}
            disabled={loadingConcorrer || loadingAlimentos}
            startIcon={loadingConcorrer ? <CircularProgress size={18} /> : <Assignment />}
          >
            {loadingConcorrer ? 'Salvando...' : 'Confirmar inscrição'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ProdutorEditais

